import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';

export interface PriceRecord30Min {
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}

export interface PriceDataResponse {
  data: PriceRecord30Min[];
  metadata: {
    requestedFrom: string;
    requestedTo: string;
    recordCount: number;
  };
}

export interface PriceDataWithMetadata {
  data: PriceRecord30Min[];
  requestedFrom: string;
  requestedTo: string;
  recordCount: number;
}

/**
 * Hook for fetching 30-minute interval price data.
 * Used for 1-month chart views.
 *
 * @param ticker - Stock ticker symbol
 * @param from - Start date (YYYY-MM-DD)
 * @param to - End date (YYYY-MM-DD)
 * @param enabled - Set false to keep the query mounted but never fetch
 */
export const useCompanyPrice30Min = (
  ticker: string | undefined,
  from: string,
  to: string,
  enabled: boolean = true
) => {
  return useQuery<PriceDataWithMetadata, Error>({
    queryKey: ['companyPrice30Min', ticker, from, to],
    queryFn: async () => {
      if (!ticker) {
        throw new Error('Ticker is required');
      }

      const { data: response } = await api.get<PriceDataResponse>('/company-price-30min', {
        params: { ticker, from, to },
      });

      return {
        data: response.data,
        requestedFrom: response.metadata.requestedFrom,
        requestedTo: response.metadata.requestedTo,
        recordCount: response.metadata.recordCount,
      };
    },
    enabled: enabled && !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes for 30-min data
  });
};
